import { useState, useEffect } from "react";
import axios from "axios";
import { Check, ArrowRight, ChevronDown } from "lucide-react";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import Navigation from "../components/Navigation";
import Footer from "../components/Footer";
import FadeIn from "../components/FadeIn";
import SEO from "../components/SEO";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const SURVEY_URL = process.env.REACT_APP_SURVEY_URL;

// ─── Primary City ───
// Priority: Live > Now Building > Coming Next
function PrimaryCity({ cities }) {
  const live = cities?.activated || [];
  const building = cities?.open || [];
  const upcoming = cities?.on_deck || [];

  let city = null;
  let phase = null;

  if (live.length > 0) {
    city = live[0];
    phase = "live";
  } else if (building.length > 0) {
    city = building[0];
    phase = "building";
  } else if (upcoming.length > 0) {
    city = upcoming[0];
    phase = "upcoming";
  }

  if (!city) return null;

  const config = {
    live: {
      label: "Live",
      dot: "#008080",
      border: "border-haevn-teal/40",
      bg: "bg-haevn-teal/5",
      headline: "Matches are now active in this city.",
      sub: "Match Monday is live. New matches are released weekly.",
      cta: "Start Your Survey",
      ctaBg: "bg-haevn-teal",
      ctaHover: "hover:bg-haevn-teal/90",
    },
    building: {
      label: "Now Building",
      dot: "#E29E0C",
      border: "border-haevn-gold/40",
      bg: "bg-haevn-gold/5",
      headline: "Be part of the first Match Monday.",
      sub: "This city is actively building its network. Join now to be matched when it goes live.",
      cta: "Start Your Survey",
      ctaBg: "bg-haevn-gold",
      ctaHover: "hover:bg-haevn-gold/90",
    },
    upcoming: {
      label: "Up Next",
      dot: "#9CA3AF",
      border: "border-gray-200",
      bg: "bg-gray-50",
      headline: "This city is next in line.",
      sub: "Join the waitlist to help bring HAEVN to your area.",
      cta: "Join Waitlist",
      ctaBg: "bg-haevn-navy",
      ctaHover: "hover:bg-haevn-navy/90",
    },
  };

  const c = config[phase];
  const linkUrl = phase === "upcoming" ? `/waitlist/${city.slug}` : SURVEY_URL;
  const isExternal = phase !== "upcoming";

  return (
    <section className="px-6 md:px-12 pb-10" data-testid="primary-city-section">
      <div className="max-w-4xl mx-auto">
        <FadeIn>
          <div className={`border-2 ${c.border} ${c.bg} p-8 md:p-10`} data-testid="primary-city-card">
            <div className="flex items-center gap-2 mb-5">
              <span className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: c.dot }} />
              <span className="text-xs font-subhead uppercase tracking-wider" style={{ color: c.dot }}>{c.label}</span>
            </div>
            <div className="md:flex md:items-end md:justify-between md:gap-8">
              <div className="mb-6 md:mb-0">
                <h2 className="font-headline text-2xl md:text-3xl text-haevn-navy leading-tight mb-1" data-testid="primary-city-name">
                  {city.name}, {city.state}
                </h2>
                <p className="text-base text-haevn-navy/80 font-body mb-3" data-testid="primary-city-headline">{c.headline}</p>
                <p className="text-sm text-gray-500 font-body leading-relaxed max-w-md">{c.sub}</p>
                {city.pool_size && (
                  <p className="text-xs text-gray-400 font-body mt-3">{city.pool_size}</p>
                )}
              </div>
              <a
                href={linkUrl}
                {...(isExternal ? { target: "_blank", rel: "noopener noreferrer" } : {})}
                className={`inline-flex items-center gap-2 ${c.ctaBg} text-white px-8 py-4 font-subhead text-base ${c.ctaHover} transition-all duration-200 group shrink-0`}
                data-testid="primary-city-cta"
              >
                {c.cta}
                <ArrowRight className="w-5 h-5 transition-transform group-hover:translate-x-1" />
              </a>
            </div>
          </div>
        </FadeIn>
      </div>
    </section>
  );
}

// ─── Live Section ───
const LiveSection = ({ cities }) => {
  const [expanded, setExpanded] = useState(false);
  const isEmpty = !cities || cities.length === 0;
  const count = cities ? cities.length : 0;
  return (
    <div className="mb-12" data-testid="section-live">
      <button onClick={() => setExpanded(!expanded)} className="flex items-center gap-3 mb-1 w-full text-left group" data-testid="toggle-live">
        <span className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: "#008080" }} />
        <h3 className="text-lg font-subhead text-gray-900">Live</h3>
        <span className="text-xs font-subhead text-white bg-gray-800 px-2.5 py-0.5 rounded-full">{count}</span>
        <ChevronDown className={`w-4 h-4 text-gray-400 ml-auto transition-transform duration-200 ${expanded ? "rotate-180" : ""}`} />
      </button>
      <p className="text-sm text-gray-500 font-body ml-5">Matches are active and update every week.</p>
      {expanded && (
        <div className="ml-5 mt-4">
          {isEmpty ? (
            <div className="py-4 border-l-2 border-haevn-teal/20 pl-5">
              <p className="text-sm text-gray-400 font-body italic">First city activates June 2026.</p>
            </div>
          ) : (
            <div className="space-y-2">
              {cities.map((city) => (
                <a
                  key={city.id}
                  href={SURVEY_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="group block p-5 border-2 border-haevn-teal/30 bg-haevn-teal/5 hover:border-haevn-teal transition-all duration-200"
                  data-testid={`city-row-${city.slug}`}
                >
                  <div className="flex items-center justify-between">
                    <div>
                      <span className="font-subhead text-base text-haevn-navy block">{city.name}, {city.state}</span>
                      {city.pool_size && <span className="text-sm text-gray-500 font-body">{city.pool_size}</span>}
                    </div>
                    <span className="text-sm text-haevn-teal font-subhead">Match Monday Active</span>
                  </div>
                </a>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// ─── Now Building Section ───
const NowBuildingSection = ({ cities }) => {
  const isEmpty = !cities || cities.length === 0;
  const count = cities ? cities.length : 0;
  return (
    <div className="mb-12" data-testid="section-now-building">
      <div className="flex items-center gap-3 mb-1">
        <span className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: "#E29E0C" }} />
        <h3 className="text-lg font-subhead text-gray-900">Now Building</h3>
        <span className="text-xs font-subhead text-white bg-gray-800 px-2.5 py-0.5 rounded-full">{count}</span>
      </div>
      <p className="text-sm text-gray-500 font-body ml-5 mb-4">Join now to be part of your city's first Match Monday.</p>
      <div className="ml-5">
        {isEmpty ? (
          <div className="py-4 border-l-2 border-haevn-gold/20 pl-5">
            <p className="text-sm text-gray-400 font-body italic">No cities building right now.</p>
          </div>
        ) : (
          <div className="space-y-2">
            {cities.map((city) => (
              <a
                key={city.id}
                href={SURVEY_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="group block p-5 border-2 border-haevn-gold/30 bg-haevn-gold/5 hover:border-haevn-gold hover:bg-haevn-gold/10 transition-all duration-200"
                data-testid={`city-row-${city.slug}`}
              >
                <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
                  <div>
                    <span className="font-subhead text-base text-haevn-navy block">{city.name}, {city.state}</span>
                    {city.pool_size && <span className="text-sm text-gray-500 font-body">{city.pool_size}</span>}
                  </div>
                  <div className="flex items-center justify-between md:justify-end gap-3">
                    {city.opens_date && <span className="text-sm text-gray-400 font-body">{city.opens_date}</span>}
                    <span className="inline-flex items-center gap-1.5 bg-haevn-gold text-white px-4 py-2 font-subhead text-sm transition-all group-hover:bg-haevn-gold/90">
                      Start Survey
                      <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
                    </span>
                  </div>
                </div>
              </a>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

// ─── Coming Next Section ───
const ComingNextSection = ({ cities }) => {
  const [expanded, setExpanded] = useState(false);
  const count = cities ? cities.length : 0;

  // Group by date
  const grouped = (cities || []).reduce((acc, city) => {
    const key = city.opens_date || "TBD";
    if (!acc[key]) acc[key] = [];
    acc[key].push(city);
    return acc;
  }, {});

  return (
    <div className="mb-12" data-testid="section-coming-next">
      <button onClick={() => setExpanded(!expanded)} className="flex items-center gap-3 mb-1 w-full text-left group" data-testid="toggle-coming-next">
        <span className="w-2.5 h-2.5 rounded-full bg-gray-400" />
        <h3 className="text-lg font-subhead text-gray-900">Coming Next</h3>
        <span className="text-xs font-subhead text-white bg-gray-800 px-2.5 py-0.5 rounded-full">{count}</span>
        <ChevronDown className={`w-4 h-4 text-gray-400 ml-auto transition-transform duration-200 ${expanded ? "rotate-180" : ""}`} />
      </button>
      <p className="text-sm text-gray-500 font-body mb-4 ml-5">
        Join the waitlist to bring HAEVN to your city.{" "}
        <a href="/join" className="text-haevn-gold font-subhead inline-flex items-center gap-1 hover:underline" data-testid="join-waitlist-cta">
          Don't see your city? <ArrowRight className="w-3.5 h-3.5" />
        </a>
      </p>
      {expanded && (
        <div className="ml-5 space-y-6">
          {Object.entries(grouped).map(([date, citiesInGroup]) => (
            <div key={date}>
              <p className="text-xs font-subhead text-gray-500 uppercase tracking-wider mb-2">{date}</p>
              <div className="space-y-1">
                {citiesInGroup.map((city) => (
                  <a
                    key={city.id}
                    href={`/waitlist/${city.slug}`}
                    className="group flex items-center justify-between py-3 px-4 -mx-4 transition-all duration-200 rounded-sm"
                    style={{ backgroundColor: "rgba(156,163,175,0.05)", borderLeft: "3px solid rgba(156,163,175,0.15)" }}
                    onMouseEnter={(e) => { e.currentTarget.style.borderLeftColor = "#9CA3AF"; }}
                    onMouseLeave={(e) => { e.currentTarget.style.borderLeftColor = "rgba(156,163,175,0.15)"; }}
                    data-testid={`city-row-${city.slug}`}
                  >
                    <div>
                      <span className="font-body block text-gray-900">{city.name}, {city.state}</span>
                      {city.pool_size && <span className="text-xs text-gray-400 font-body">{city.pool_size}</span>}
                    </div>
                    <div className="flex items-center gap-2">
                      {city.opens_date && <span className="text-sm text-gray-400 font-body">{city.opens_date}</span>}
                      <ArrowRight className="w-4 h-4 text-gray-300 opacity-0 group-hover:opacity-100 transition-opacity" />
                    </div>
                  </a>
                ))}
              </div>
            </div>
          ))}
          <p className="text-xs text-red-500 font-body">* Dates may move up based on waitlist activity.</p>
        </div>
      )}
    </div>
  );
};

// ─── Capture Form ───
const CaptureForm = () => {
  const [email, setEmail] = useState("");
  const [zipCode, setZipCode] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [message, setMessage] = useState(null);
  const [errors, setErrors] = useState({});
  const [focusedField, setFocusedField] = useState(null);

  const validateForm = () => {
    const newErrors = {};
    if (!email || !email.includes("@")) newErrors.email = "Please enter a valid email address.";
    if (!zipCode || zipCode.length !== 5 || !/^\d+$/.test(zipCode)) newErrors.zip = "Please enter a valid 5-digit ZIP code.";
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setMessage(null);
    if (!validateForm()) return;
    setIsSubmitting(true);
    try {
      const response = await axios.post(`${API}/zip-interest`, { email, zip_code: zipCode });
      setMessage(response.data.message);
      setEmail(""); setZipCode(""); setErrors({});
    } catch (err) {
      setErrors({ form: err.response?.data?.detail || "Something went wrong. Please try again." });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div data-testid="capture-form-container" className="max-w-sm">
      <form onSubmit={handleSubmit} className="space-y-3">
        <div className="relative">
          <Input
            type="email" value={email}
            onChange={(e) => setEmail(e.target.value)}
            onFocus={() => setFocusedField("email")}
            onBlur={() => setFocusedField(null)}
            placeholder="Email address"
            className={`bg-white border-gray-300 text-haevn-navy px-4 py-3 text-base font-body transition-all duration-300 rounded-none placeholder:text-gray-400 ${focusedField === "email" ? "border-haevn-navy" : ""} ${errors.email ? "border-red-400" : ""} focus:ring-0 focus-visible:ring-0 focus-visible:ring-offset-0`}
            data-testid="email-input"
          />
          {errors.email && <span className="absolute -bottom-5 left-0 text-xs text-red-500">{errors.email}</span>}
        </div>
        <div className="relative">
          <Input
            type="text" value={zipCode}
            onChange={(e) => setZipCode(e.target.value.replace(/\D/g, "").slice(0, 5))}
            onFocus={() => setFocusedField("zip")}
            onBlur={() => setFocusedField(null)}
            placeholder="ZIP code"
            className={`bg-white border-gray-300 text-haevn-navy px-4 py-3 text-base font-body transition-all duration-300 rounded-none placeholder:text-gray-400 ${focusedField === "zip" ? "border-haevn-navy" : ""} ${errors.zip ? "border-red-400" : ""} focus:ring-0 focus-visible:ring-0 focus-visible:ring-offset-0`}
            data-testid="zip-code-input" maxLength={5}
          />
          {errors.zip && <span className="absolute -bottom-5 left-0 text-xs text-red-500">{errors.zip}</span>}
        </div>
        <Button type="submit" disabled={isSubmitting} className="w-full bg-haevn-navy text-white px-6 py-3 font-subhead text-base hover:bg-haevn-navy/90 transition-all duration-300 rounded-none h-auto group" data-testid="submit-capture-button">
          <span className="flex items-center justify-center gap-2">
            {isSubmitting ? "Adding..." : "Add Me"}
            <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
          </span>
        </Button>
        {errors.form && <div className="text-sm text-red-500" data-testid="form-error">{errors.form}</div>}
      </form>
      {message && (
        <div className="mt-4 p-4 bg-haevn-teal/10 border border-haevn-teal/30 text-haevn-navy flex items-start gap-3" data-testid="success-message">
          <div className="w-5 h-5 rounded-full bg-haevn-teal/30 flex items-center justify-center flex-shrink-0 mt-0.5">
            <Check className="w-3 h-3 text-haevn-teal" />
          </div>
          <span className="font-body text-sm">{message}</span>
        </div>
      )}
    </div>
  );
};

// ─── System State Summary ───
function SystemState({ cities }) {
  const liveCount = cities?.activated?.length || 0;
  const buildingCount = cities?.open?.length || 0;
  const upcomingCount = cities?.on_deck?.length || 0;
  const total = liveCount + buildingCount + upcomingCount;

  return (
    <div className="flex items-center gap-6 flex-wrap" data-testid="system-state">
      <div className="flex items-center gap-2">
        <span className="w-2 h-2 rounded-full bg-haevn-teal" />
        <span className="text-sm text-gray-500 font-body"><span className="font-subhead text-haevn-navy">{liveCount}</span> live</span>
      </div>
      <div className="flex items-center gap-2">
        <span className="w-2 h-2 rounded-full bg-haevn-gold" />
        <span className="text-sm text-gray-500 font-body"><span className="font-subhead text-haevn-navy">{buildingCount}</span> building</span>
      </div>
      <div className="flex items-center gap-2">
        <span className="w-2 h-2 rounded-full bg-gray-400" />
        <span className="text-sm text-gray-500 font-body"><span className="font-subhead text-haevn-navy">{upcomingCount}</span> coming</span>
      </div>
      <span className="text-xs text-gray-400 font-body">{total} cities in the network</span>
    </div>
  );
}

// ─── Main Page ───
export default function ActiveCitiesPage() {
  const [cities, setCities] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get(`${API}/cities`)
      .then((res) => setCities(res.data))
      .catch((err) => console.error("Failed to fetch cities:", err))
      .finally(() => setLoading(false));
  }, []);

  return (
    <div className="min-h-screen bg-white" data-testid="active-cities-page">
      <SEO
        title="Active Cities"
        path="/cities"
        description="HAEVN expands city by city. See which cities are live, building, and coming next. Join the waitlist to bring HAEVN to your city."
      />
      <Navigation />

      {/* Hero */}
      <section className="pt-10 pb-8 px-6 md:px-12" data-testid="hero-section">
        <div className="max-w-4xl mx-auto">
          <FadeIn>
            <p className="text-xs font-subhead uppercase tracking-widest text-gray-400 mb-4">Network Status</p>
            <h1 className="font-headline text-4xl md:text-5xl text-haevn-navy mb-3">Active Cities</h1>
            <p className="text-base md:text-lg text-gray-500 font-body mb-5">
              HAEVN expands city by city. Each city operates as its own local network. Matches go live once critical mass is reached.
            </p>
          </FadeIn>
          {!loading && cities && <FadeIn delay={100}><SystemState cities={cities} /></FadeIn>}
        </div>
      </section>

      {/* Primary City */}
      {!loading && cities && <PrimaryCity cities={cities} />}

      {/* Network Sections */}
      <section className="py-6 px-6 md:px-12" data-testid="network-status-section">
        <div className="max-w-4xl mx-auto">
          {loading ? (
            <p className="text-gray-400 font-body" data-testid="loading-cities">Loading...</p>
          ) : cities ? (
            <>
              <LiveSection cities={cities.activated} />
              <NowBuildingSection cities={cities.open} />
              <ComingNextSection cities={cities.on_deck} />
            </>
          ) : (
            <p className="text-gray-400 font-body" data-testid="error-cities">Unable to load cities.</p>
          )}
        </div>
      </section>

      {/* Capture */}
      <section className="py-12 px-6 md:px-12 bg-[#E8E6E3]" data-testid="capture-section">
        <div className="max-w-4xl mx-auto">
          <div className="grid md:grid-cols-2 gap-10 items-start">
            <div>
              <h2 className="font-headline text-xl md:text-2xl text-haevn-navy mb-3">Bring HAEVN to your city.</h2>
              <p className="text-gray-600 font-body leading-relaxed">
                Enter your email and ZIP code.<br />We'll notify you as your city approaches launch.
              </p>
              <div className="flex items-center gap-5 mt-5">
                <div className="flex items-center gap-2 text-sm">
                  <div className="w-4 h-4 rounded-full bg-haevn-teal/20 flex items-center justify-center">
                    <Check className="w-2.5 h-2.5 text-haevn-teal" />
                  </div>
                  <span className="text-gray-500 font-body">No spam</span>
                </div>
                <div className="flex items-center gap-2 text-sm">
                  <div className="w-4 h-4 rounded-full bg-haevn-gold/20 flex items-center justify-center">
                    <Check className="w-2.5 h-2.5 text-haevn-gold" />
                  </div>
                  <span className="text-gray-500 font-body">First to know</span>
                </div>
              </div>
            </div>
            <CaptureForm />
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
